import { useEffect, useRef, useState } from 'react'
import { useAddCatalogueItem } from '../basket/potentials'
import { useTranslation } from '../localization/useTranslation'
import { PhotoFinder } from '../pages/PhotoFinder'
import { useCategoryOptions } from '../search/categories'
import { type SearchProduct, searchCatalogue } from '../search/searchClient'
import { useSearchCurrency } from '../search/searchCurrency'
import { formatCategoryLabel } from '../util/category'
import { isDisplayableProductMetadata } from '../util/productMetadata'
import { useAuthStore } from '../store/auth'
import { Glyph, IconArrowLeft, IconCamera, IconPlus, IconSearch } from '../ui/icons'
// Inlined as a base64 data URI (?inline): the widget embeds on third-party storefronts, so an
// external asset path would resolve against the wrong origin. Small enough to travel in the bundle.
import careLabelSrc from '../assets/carelabel.png?inline'
import { getSearchLabelImage } from '../providers/embedConfig'
import { useToast } from '../ui/toast'
import { useMoneyFmt } from '../util/useMoney'
import { MatchDetailDialog } from './MatchDetailDialog'
import { SearchField } from './SearchField'

/**
 * The "finder" card (mockup): two ways to add an item. "Search catalogue" runs real debounced
 * text search against `tradeinsearch2` and adds matches via PotentialCreate; "Add by photo" is
 * the visual-search flow. Clicking a result opens the inspect dialog (gallery + confirm); the
 * trailing button is a one-tap quick-add. Used by the no-feed step and the feed's manual-add view.
 */
type Method = 'search' | 'photo'

const MIN_QUERY = 3
/** Skeleton rows to show while a search is in flight (roughly a typical short result set). */
const SEARCH_SKELETON_ROWS = 4

export function Finder({ onBack }: { onBack?: () => void }) {
  const config = useAuthStore((s) => s.config)
  const shopId = useAuthStore((s) => s.shopId)
  const { add } = useAddCatalogueItem()
  const showToast = useToast((s) => s.show)
  const { t } = useTranslation()
  // Ask for the shop's base currency so results share the same basis as Potential offers; display
  // converts to the consumer's currency (see useMoneyFmt). Always a real currency — the endpoint's
  // own default must not decide what a match is priced in.
  const searchCurrency = useSearchCurrency()

  // Photo is the primary way to add an item (most items aren't in a past-orders feed), so it leads.
  const [method, setMethod] = useState<Method>('photo')
  const [term, setTerm] = useState('')
  const [results, setResults] = useState<SearchProduct[]>([])
  const [searching, setSearching] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  // Whether the server has further pages beyond what's loaded — drives the "Show more results" button.
  const [hasMore, setHasMore] = useState(false)
  // Highest page loaded for the current query, so "Show more" fetches the next one and appends.
  const [page, setPage] = useState(1)
  const [searchFailed, setSearchFailed] = useState(false)
  // The query the currently-shown results belong to — set when a search runs, so the "no matches"
  // note only appears after an actual search (not while the user is still typing). Also the query
  // "Show more" pages against, so it stays stable even if the input text changes afterwards.
  const [searchedQuery, setSearchedQuery] = useState('')
  const [addingId, setAddingId] = useState<string | null>(null)
  const searchInput = useRef<HTMLInputElement>(null)

  const canSearch = Boolean(shopId) && Boolean(config.productSearchEnabled)
  // Categories-only shops (no product search, but categories exist) get a single flow: take a photo
  // → pick a category. No photo/search method toggle, just the category-mode finder. Gated on real
  // categories existing, not just the `wholeSaleActive` flag.
  const { hasCategories } = useCategoryOptions()
  const categoryOnly = !config.productSearchEnabled && hasCategories
  const fmt = useMoneyFmt()

  // The single card title (beside the toggle) describes the ACTIVE method, so it isn't a third copy of
  // the page title — it replaces the old in-body photo/search intros. Category-only shops are photo.
  const onPhoto = categoryOnly || method === 'photo'
  const headTitle = onPhoto ? t('photo.dropTitle') : t('finder.searchTitle')
  const headSub = onPhoto ? t('photo.dropHint') : t('finder.searchSub')

  // Search is explicitly submitted (by Enter or the field button), not search-as-you-type: the
  // customer types a full SKU/EAN/name and submits. A skeleton is shown while the query is in flight.
  const runSearch = async (raw: string) => {
    const q = raw.trim()
    setSearchedQuery(q)
    setPage(1)
    setHasMore(false)
    if (q.length < MIN_QUERY || !canSearch) {
      setResults([])
      setSearching(false)
      return
    }
    setSearching(true)
    setSearchFailed(false)
    try {
      const found = await searchCatalogue({
        shopId: shopId!,
        query: q,
        language: config.locale,
        currency: searchCurrency,
      })
      setResults(found.items)
      setHasMore(found.hasMore)
    } catch {
      setSearchFailed(true)
      setResults([])
    } finally {
      setSearching(false)
    }
  }

  // Fetch the next page for the current query and append it (the endpoint pages at 25/query, up to a
  // 60 server cap). Keyed off `searchedQuery` so it pages the submitted query, not any newer input.
  const loadMore = async () => {
    if (loadingMore || !hasMore) return
    const next = page + 1
    setLoadingMore(true)
    try {
      const found = await searchCatalogue({
        shopId: shopId!,
        query: searchedQuery,
        language: config.locale,
        currency: searchCurrency,
        page: next,
      })
      // De-dupe defensively in case a page boundary overlaps; masterVariantId is the stable identity.
      setResults((prev) => {
        const seen = new Set(prev.map((p) => p.masterVariantId))
        return [...prev, ...found.items.filter((p) => !seen.has(p.masterVariantId))]
      })
      setHasMore(found.hasMore)
      setPage(next)
    } catch {
      // Leave the loaded results in place; a failed "show more" just keeps the button for a retry.
      setHasMore(true)
    } finally {
      setLoadingMore(false)
    }
  }

  // Clearing the field clears the results (and the care-label hint returns); avoids stale matches
  // lingering under an empty box after an Enter-driven search.
  useEffect(() => {
    if (term.trim() === '') {
      setResults([])
      setSearchFailed(false)
      setSearchedQuery('')
      setHasMore(false)
      setPage(1)
    }
  }, [term])

  const onSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // Blur so the mobile keyboard closes — otherwise it covers the results the customer just asked
    // for. Re-tapping the field selects the text (onFocus) so a new query is still easy to type.
    searchInput.current?.blur()
    void runSearch(term)
  }

  const onAdd = async (product: SearchProduct) => {
    setAddingId(product.masterVariantId)
    try {
      await add(product)
      showToast(t('finder.added', { name: product.name, amount: fmt(product.tradeInValue) }))
    } finally {
      setAddingId(null)
    }
  }

  return (
    <div className="finder">
      <div className="finder-head">
        <span className="finder-title-group">
          {onBack && (
            <button
              type="button"
              className="finder-back"
              onClick={onBack}
              aria-label={t('finder.backToOrders')}
            >
              <IconArrowLeft />
            </button>
          )}
          <span className="finder-titles">
            <span className="finder-title">{headTitle}</span>
            {headSub && <span className="finder-sub">{headSub}</span>}
          </span>
        </span>
        {/* No method toggle for categories-only shops — there's a single photo→category flow. */}
        {!categoryOnly && (
          <span className="method" role="group" aria-label={t('finder.photo')}>
            <button type="button" aria-pressed={method === 'photo'} onClick={() => setMethod('photo')}>
              <IconCamera />
              {t('finder.photo')}
            </button>
            <button
              type="button"
              aria-pressed={method === 'search'}
              onClick={() => setMethod('search')}
            >
              <IconSearch />
              {t('finder.search')}
            </button>
          </span>
        )}
      </div>

      <div className="finder-body">
        {categoryOnly ? (
          <PhotoFinder categoryOnly />
        ) : method === 'search' ? (
          !canSearch ? (
            <p className="res-note">{t('finder.unavailable')}</p>
          ) : (
            <>
              {/* One form path for both Enter and the explicit end-of-field search button. */}
              <SearchField
                inputRef={searchInput}
                value={term}
                onChange={setTerm}
                placeholder={t('finder.searchPlaceholder')}
                label={t('finder.search')}
                onSubmit={onSearchSubmit}
              />
              {/* Care-label helper: where to find the SKU/style number. Hidden as soon as the
                  customer starts typing (their query takes the space). */}
              {term.trim() === '' && (
                <div className="carelabel-hint">
                  {/* Brand-overridable via tradeInConfig.searchLabelImage; defaults to the care label. */}
                  <img className="carelabel-img" src={getSearchLabelImage() ?? careLabelSrc} alt="" />
                  {/* Text is overridable via the dictionary; an empty override hides it entirely
                      (e.g. the SKU/care-label hint doesn't make sense for jewelry). */}
                  {t('finder.carelabelHint') && <p>{t('finder.carelabelHint')}</p>}
                </div>
              )}
              <div className="results">
                {/* Skeleton placeholder rows while the query is in flight, so it's clear something
                    is happening (replaces a bare "Searching…" line). */}
                {searching &&
                  Array.from({ length: SEARCH_SKELETON_ROWS }, (_, i) => (
                    <div className="res res-skeleton" key={i} aria-hidden>
                      <span className="sk sk-thumb" />
                      <span className="sk-meta">
                        <span className="sk sk-line sk-line-1" />
                        <span className="sk sk-line sk-line-2" />
                      </span>
                      <span className="sk sk-val" />
                    </div>
                  ))}
                {searchFailed && <p className="res-note">{t('finder.searchFailed')}</p>}
                {!searching &&
                  !searchFailed &&
                  searchedQuery.length >= MIN_QUERY &&
                  results.length === 0 && (
                    <p className="res-note">{t('finder.noMatches', { query: searchedQuery })}</p>
                  )}
                {!searching &&
                  results.map((product) => (
                    <SearchResultRow
                      key={product.masterVariantId}
                      product={product}
                      defaultImage={config.defaultImage}
                      adding={addingId === product.masterVariantId}
                      onAdd={() => onAdd(product)}
                    />
                  ))}
                {/* More matches exist on the server than are loaded — pull the next page in place. */}
                {!searching && hasMore && (
                  <button
                    type="button"
                    className="btn btn-ghost show-more"
                    onClick={loadMore}
                    disabled={loadingMore}
                  >
                    {loadingMore ? t('finder.loadingMore') : t('finder.showMore')}
                  </button>
                )}
              </div>
            </>
          )
        ) : (
          <PhotoFinder />
        )}
      </div>
    </div>
  )
}

interface SearchResultRowProps {
  product: SearchProduct
  defaultImage?: string
  adding: boolean
  onAdd: () => void
}

function SearchResultRow({ product, defaultImage, adding, onAdd }: SearchResultRowProps) {
  const { t } = useTranslation()
  const fmt = useMoneyFmt()
  const eligible = product.tradeInValue > 0
  const image = product.image || defaultImage
  // Prefer a meaningful category label; fall back to colour. Both can be placeholders.
  const catName = product.category?.name
  const sub =
    catName && catName.toLowerCase() !== 'unknown'
      ? formatCategoryLabel(catName)
      : isDisplayableProductMetadata(product.color)
        ? product.color
        : ''

  const content = (
    <>
      <span className="thumb">{image ? <img src={image} alt="" /> : <Glyph name="shirt" />}</span>
      <span className="meta">
        <div className="nm">{product.name}</div>
        {sub && <div className="sub">{sub}</div>}
      </span>
      <span className="val">
        <div className="est">{t('finder.estReward')}</div>
        <div className="amt num">
          {eligible ? fmt(product.tradeInValue) : t('finder.notEligible')}
        </div>
      </span>
    </>
  )

  return (
    <div className="res">
      {eligible ? (
        <MatchDetailDialog
          trigger={
            <button type="button" className="res-main">
              {content}
            </button>
          }
          product={product}
          defaultImage={defaultImage}
          backLabel={t('match.backToResults')}
          onConfirm={onAdd}
        />
      ) : (
        <div className="res-main res-main--static">{content}</div>
      )}
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        disabled={!eligible || adding}
        onClick={onAdd}
      >
        <IconPlus />
        {adding ? t('finder.adding') : t('finder.add')}
      </button>
    </div>
  )
}
